import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'next-themes';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { CookieConsent } from '@/components/CookieConsent';
import { LanguageSuggestionBanner } from '@/components/LanguageSuggestionBanner';
import { LeadMagnetPopup } from '@/components/LeadMagnetPopup';

import i18n, { restoreSavedLanguage } from '@/config/i18n';
import { getI18nForLang, isLangSkippablePath, urlLangOf } from '@/components/LangWrapper';
import { useTranslation } from 'react-i18next';
import HreflangTags from '@/components/HreflangTags';
import RouteTracker from '@/components/RouteTracker';
import HostBranding, { isBrandedHost } from '@/components/HostBranding';
import BrandedHostGuard from '@/components/BrandedHostGuard';
import { AuthProvider } from '@/hooks/useAuth';
import { UserPreferencesProvider } from '@/hooks/useUserPreferences';
import { CurrencyProvider } from '@/context/CurrencyContext';
import { SchemaScript, generateOrganizationSchema, generateWebSiteSchema } from '@/lib/seoHelpers';
// Both marketing shells load app-wide, not just on the pages that import them
// themselves: the light system below themes every marketing route, and
// cinematic.css still owns the app surfaces (designer, dashboard, admin,
// portals) plus the shared `cin-*` component vocabulary the light scope
// re-points by token.
import '@/pages/cinematic.css';
import '@/styles/todai-marketing.css';
import { MarketingToneContext } from '@/components/MarketingToneContext';

const queryClient = new QueryClient();

export default function RootLayout() {
  const { i18n: i18nInstance } = useTranslation();
  const location = useLocation();

  // SSG renders routes CONCURRENTLY against ONE i18n singleton. Prefixed
  // routes are protected by LangWrapper's per-language clones, but the
  // unprefixed EN pages read the singleton — whichever language a sibling
  // render set last won, which is how the production /pricing page shipped
  // with 100% Polish copy under lang="en". During SSR, bind the provider to
  // the URL's language clone instead (skip app/protected paths — their
  // language comes from the user preference mutating the singleton). On the
  // client nothing changes: one render at a time, no race, and the saved-
  // language restore keeps working through the singleton.
  const routedI18n =
    import.meta.env.SSR && !isLangSkippablePath(location.pathname)
      ? getI18nForLang(urlLangOf(location.pathname))
      : i18n;

  // Restore saved language after hydration to avoid mismatch
  useEffect(() => {
    restoreSavedLanguage();
  }, []);

  // Reveal the page once the SPA has mounted + routed. On a branded host
  // index.html hides <body> (.branded-boot) to avoid flashing the pre-rendered
  // marketing landing before the guard redirects to /auth or /dashboard.
  useEffect(() => {
    document.documentElement.classList.remove('branded-boot');
  }, []);

  // Keep <html dir> in sync with current language (lang is set by HreflangTags via Helmet)
  useEffect(() => {
    document.documentElement.dir = i18nInstance.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18nInstance.language]);

  // ── Global marketing scope ─────────────────────────────────────────────
  // Every marketing/content route renders inside the LIGHT .todai-marketing
  // brand theme — the same system as the Todae store: light grey paper, white
  // cards, one blue. The wrapper re-points the shadcn tokens, the extended
  // cinematic tokens the shared `cin-*` components read, and the editorial
  // --color-* tokens the pelco header/footer read, so legacy pages restyle by
  // inheritance without JSX changes.
  //
  // App surfaces keep the dark "Instrument" system they were built for and are
  // excluded here: the designer, dashboard, client portal, admin console, org
  // console, settings, the installer portal and the signed-in job/order
  // screens. White-label branded hosts keep their own light editorial look.
  const APP_SURFACE_PREFIXES = [
    '/cctv-design', '/dashboard', '/p/', '/admin', '/organization',
    '/settings', '/accept-invite', '/demo', '/try',
    '/installer', '/my-installations', '/checkout-success', '/api-docs',
  ];
  const isAppSurface = APP_SURFACE_PREFIXES.some(
    (p) => location.pathname === p.replace(/\/$/, '') || location.pathname.startsWith(p),
  );
  const brandedHost = typeof window !== 'undefined' && isBrandedHost();
  // `/installer-jobs` is a public marketing page; `/installer` is the portal.
  const isMarketingShell =
    (!isAppSurface || location.pathname.startsWith('/installer-jobs')) && !brandedHost;

  // The <html> class the app forces. It used to be `dark` everywhere (the
  // designer needs it) which left every marketing page rendering its `dark:`
  // Tailwind variants over the light system — white text on white paper, and a
  // hydration flip away from the prerendered HTML, which carries no class at
  // all. On a light route the forced theme is `light`, so the base utilities
  // win and the hydrated page matches the page that read without JS. App
  // surfaces keep `dark`.
  const forcedTheme = isMarketingShell || brandedHost ? 'light' : 'dark';

  return (
    <ThemeProvider attribute="class" forcedTheme={forcedTheme} defaultTheme="dark" storageKey="todaidesign_theme" enableSystem={false}>
      <I18nextProvider i18n={routedI18n}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <UserPreferencesProvider>
              <CurrencyProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <HreflangTags />
                <SchemaScript schema={generateOrganizationSchema()} />
                <SchemaScript schema={generateWebSiteSchema()} />
                <RouteTracker />
                <HostBranding />
                <LanguageSuggestionBanner />
                <Suspense fallback={<div className="min-h-screen" />}>
                  <BrandedHostGuard>
                    <MarketingToneContext.Provider value={isMarketingShell ? 'light' : 'dark'}>
                      <div className={isMarketingShell ? 'todai-marketing' : undefined}>
                        <Outlet />
                      </div>
                    </MarketingToneContext.Provider>
                  </BrandedHostGuard>
                </Suspense>
                <CookieConsent />
                {/* Owned-channel lead capture — marketing only. Each popup
                    self-gates (anonymous + session + route blacklist); the
                    branded-host guard keeps them off white-label client portals.
                    They render null until triggered, so SSR output is unchanged. */}
                {typeof window !== 'undefined' && !isBrandedHost() && (
                  <>
                    <LeadMagnetPopup />
                    {/* ExitIntentPopup disabled — its top-of-viewport mouseleave
                        trigger fired every time the cursor moved to the address
                        bar / tabs, so it felt like it popped up immediately and
                        read as spammy. Re-enable with a dwell-time guard if we
                        want exit-intent capture back. */}
                  </>
                )}
              </TooltipProvider>
              </CurrencyProvider>
            </UserPreferencesProvider>
          </AuthProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
}
